import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';

import { ReactComponent as Close } from '../../assets/icons/close.svg';
import { ReactComponent as Edit } from '../../assets/icons/edit.svg';
import { ReactComponent as Impressum } from '../../assets/icons/impressum.svg';
import { ReactComponent as VerifiedBadge } from '../../assets/icons/verified-badge.svg';

import UserImpressum from '../../components/Cobe/Impressum/UserImpressum';

import { useConfiguration } from '../../context/configurationContext';

import {
  isForbiddenError,
  isNotFoundError
} from '../../util/errors';


import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { richText } from '../../util/richText';

import {
  REVIEW_TYPE_OF_CUSTOMER,
  REVIEW_TYPE_OF_PROVIDER
} from '../../util/types';

import {
  parse
} from '../../util/urlHelpers';

import {
  hasPermissionToViewData,
  isUserAuthorized
} from '../../util/userHelpers';

import {
  AvatarMedium,
  Button,
  H4, IconSpinner,
  InlineTextButton,
  NamedLink,
  NamedRedirect,
  Page,
  Reviews
} from '../../components';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import Footer from '../../components/Cobe/Footer/Footer';
import TabbedProductList from '../../components/Cobe/TabbedProductList/TabbedProductList';
import LayoutSingleColumn from '../../components/LayoutComposer/LayoutSingleColumn/LayoutSingleColumn';
import Modal from '../../components/Modal/Modal';
import BlockUser from './BlockUser/BlockUser';

import {
  closeListing,
  discardDraftListing,
} from './ProfilePage.duck';

import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;
// Same threshold UserCard.js uses for the listing page's "about the
// provider" bio - past this many characters a bio reliably runs past
// BIO_COLLAPSED_LINES lines, so it's worth collapsing behind a toggle
// instead of letting an unbounded amount of user-entered text (this is
// free text, nothing stops someone pasting a wall of it) push the rest
// of the profile page down indefinitely.
const BIO_COLLAPSED_LENGTH = 170;

/* ===================================================
   LOADER
=================================================== */

const PageSpinner = () => (
  <div className={css.pageLoader}>
    <IconSpinner className={css.bigSpinner} />
  </div>
);

/* ===================================================
   REVIEWS
=================================================== */

export const ReviewsErrorMaybe = ({ queryReviewsError }) =>
  queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;

  const reviewsOfProvider = reviews.filter(
    r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER
  );

  const reviewsOfCustomer = reviews.filter(
    r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER
  );

  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>

      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />

      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>

      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews } = props;

  return (
    <div className={css.desktopReviews}>
      <Reviews reviews={reviews} />
    </div>
  );
};

/* ===================================================
   MAIN CONTENT
=================================================== */

export const MainContent = props => {
  const config = useConfiguration();
  const history = useHistory();
  const location = useLocation();

  const [mounted, setMounted] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  const {
    userShowError,
    bio,
    listings,
    listingsNotForSale,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    user,
    currentUser,
    isCurrentUser,
    onCloseListing,
    onDiscardDraftListing,

    forSalePagination,
    notForSalePagination,

    userListingForSaleRefsInProgress,
    userListingNotForSaleRefsInProgress,
  } = props;

  useEffect(() => {
    setMounted(true);
  }, []);

  const hasMatchMedia =
    typeof window !== 'undefined' && window.matchMedia;

  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(
          `(max-width:${MAX_MOBILE_SCREEN_WIDTH}px)`
        ).matches
      : true;

  const urlParams = parse(location.search || '');
  const initialTab = urlParams.tab || 'for-sale';

  if (
    userListingForSaleRefsInProgress ||
    userListingNotForSaleRefsInProgress
  ) {
    return <PageSpinner />;
  }

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const hasBio = !!bio;
  const needsBioExpansion = hasBio && bio.length >= BIO_COLLAPSED_LENGTH;

  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const isBusinessUser =
    user?.attributes?.profile?.publicData?.userType ===
    'business-user';

  const changePage = (tab, page) => {
    history.push({
      pathname: location.pathname,
      search: `?tab=${tab}&page=${page}`,
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleHeaderActionButton = () => (
    <>
      {isCurrentUser && (
        <NamedLink
          name="ProfileSettingsPage"
          className={css.secondaryButton}
        >
          <FormattedMessage id="ListingPage.editProfileLink" />
          <Edit />
        </NamedLink>
      )}

      {!isCurrentUser && isBusinessUser && (
        <Button
          className={css.secondaryButton}
          onClick={() => setOpen(true)}
        >
          <FormattedMessage id="ProfileSettingsForm.Impressum" />
          <Impressum />
        </Button>
      )}
    </>
  );

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          usePortal
          onClose={() => setOpen(false)}
        >
          <div className={css.modalHeader}>
            <button
              className={css.closeButton}
              onClick={() => setOpen(false)}
            >
              <Close />
            </button>

            <h2 className={css.modalTitle}>
              <FormattedMessage id="ProfileSettingsForm.Impressum" />
            </h2>
          </div>

          <UserImpressum publicData={user?.attributes?.profile?.publicData} />
        </Modal>
      )}

      <header className={css.header}>
        <div className={css.headerContent}>
          <AvatarMedium
            className={css.avatar}
            user={user}
            disableProfileLink
          />

          <div className={css.userDescription}>
            <div className={css.userBadge}>
              <span className={css.username}>
                {
                  user?.attributes?.profile?.publicData
                    ?.username
                }
              </span>

              <div className={css.verifiedBadge}>
                {user?.attributes?.profile?.metadata?.verified_by_marketplace && (
                  <VerifiedBadge />
                )}
              </div>
            </div>
          </div>

          <div
            className={classNames(
              css.headerAction,
              css.desktop
            )}
          >
            {handleHeaderActionButton()}
          </div>
        </div>

        {hasBio ? (
          <div className={css.bioSection}>
            <p
              className={classNames(css.bio, {
                [css.bioCollapsed]: !isBioExpanded,
              })}
            >
              {bioWithLinks}
            </p>

            {needsBioExpansion && (
              <InlineTextButton
                rootClassName={css.showBioToggle}
                onClick={() => setIsBioExpanded(prev => !prev)}
              >
                <FormattedMessage
                  id={isBioExpanded ? 'UserCard.showLessBioLink' : 'UserCard.showFullBioLink'}
                />
              </InlineTextButton>
            )}
          </div>
        ) : null}

        <div
          className={classNames(
            css.headerAction,
            css.mobile
          )}
        >
          {handleHeaderActionButton()}
        </div>

        {/* Rendered once here (not inside handleHeaderActionButton, which is called once
            per breakpoint - .desktop and .mobile above - and would otherwise double-mount
            this component's own modal/state). Visible for both breakpoints via its own CSS,
            not a duplicated .desktop/.mobile pair like the buttons above. */}
        {!isCurrentUser && (
          <BlockUser
            profileUserId={user?.id?.uuid}
            profileUserDisplayName={
              user?.attributes?.profile?.publicData?.username ||
              user?.attributes?.profile?.displayName
            }
            currentUser={currentUser}
          />
        )}
      </header>

      <TabbedProductList
        onLightBackground
        hideActionButton
        fullWidthTabs
        initialTab={initialTab}
        location={location}
        history={history}
        isProfilePage
        onCloseListing={onCloseListing}
        onDiscardDraftListing={onDiscardDraftListing}
        listings={[
          {
            id: 'for-sale',
            listings,
            count:
              forSalePagination?.totalItems ||
              listings.length,
            isCurrentUser,

            currentPage:
              forSalePagination?.page || 1,

            totalPages:
              forSalePagination?.totalPages || 1,

            pagination: forSalePagination,

            onPageChange: page =>
              changePage('for-sale', page),

            tabNameIntlId:
              'UserProfile.Tabs.ForSale',

            noListingsTitle:
              'UserProfile.Jersies.NoListingsTitle',

            noListingsText:
              'UserProfile.Jersies.NoListingsText',
          },

          {
            id: 'not-for-sale',
            listings: listingsNotForSale,
            count:
              notForSalePagination?.totalItems ||
              listingsNotForSale.length,

            isCurrentUser,

            currentPage:
              notForSalePagination?.page || 1,

            totalPages:
              notForSalePagination?.totalPages || 1,

            pagination: notForSalePagination,

            onPageChange: page =>
              changePage(
                'not-for-sale',
                page
              ),

            tabNameIntlId:
              'UserProfile.Tabs.NotForSale',

            noListingsTitle:
              'UserProfile.Jersies.NoListingsTitle',

            noListingsText:
              'UserProfile.Jersies.NoListingsText',
          },

          {
            id: 'reviews',
            listings: reviews,
            count: reviews.length,
            isReviews: true,
            queryReviewsError,
            tabNameIntlId:
              'UserProfile.Tabs.Reviews',
            noListingsTitle:
              'UserProfile.Reviews.NoListingsTitle',
            noListingsText:
              'UserProfile.Reviews.NoListingsText',
          },
        ]}
      />

      {/* {isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
        />
      ) : (
        <DesktopReviews reviews={reviews} />
      )} */}
    </>
  );
};

/* ===================================================
   PAGE
=================================================== */

export const ProfilePageComponent = props => {
  const intl = useIntl();
  const config = useConfiguration();

  const {
    scrollingDisabled,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;

  const profileUser = useCurrentUser
    ? currentUser
    : user;

  if (!profileUser && !userShowError) {
    return <PageSpinner />;
  }

  if (isNotFoundError(userShowError)) {
    return <NotFoundPage />;
  }

  if (isForbiddenError(userShowError)) {
    return <NamedRedirect name="SignupPage" />;
  }

  const profile = profileUser?.attributes?.profile;
  const displayName =
    profile?.publicData?.username || profile?.displayName || '';

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={intl.formatMessage(
        { id: 'ProfilePage.schemaTitle' },
        { name: displayName, marketplaceName: config.marketplaceName }
      )}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<Footer />}
        mainColumnClassName={css.mainWrap}
      >
        {/* <BackgroundLines /> */}

        <div className={css.container}>
          <MainContent
            user={profileUser}
            currentUser={currentUser}
            bio={profileUser?.attributes?.profile?.bio}
            {...rest}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/* ===================================================
   REDUX
=================================================== */

const mapDispatchToProps = dispatch => ({
  onCloseListing: id =>
    dispatch(closeListing(id)),

  onDiscardDraftListing: id =>
    dispatch(discardDraftListing(id)),
});

const mapStateToProps = state => {
  const { currentUser } = state.user;

  const {
    userId,
    userShowError,
    queryListingsError,

    userListingForSaleRefs,
    userListingNotForSaleRefs,

    reviews,
    queryReviewsError,

    forSalePagination,
    notForSalePagination,

    userListingForSaleRefsInProgress,
    userListingNotForSaleRefsInProgress,
  } = state.ProfilePage;

  const userMatches =
    getMarketplaceEntities(state, [
      { type: 'user', id: userId },
    ]);

  const user =
    userMatches.length === 1
      ? userMatches[0]
      : null;

  const isCurrentUser =
    userId?.uuid === currentUser?.id?.uuid;

  const useCurrentUser =
    isCurrentUser &&
    !(
      isUserAuthorized(currentUser) &&
      hasPermissionToViewData(
        currentUser
      )
    );

  return {
    scrollingDisabled:
      isScrollingDisabled(state),

    currentUser,
    useCurrentUser,
    user,

    isCurrentUser,

    userShowError,
    queryListingsError,

    listings: getMarketplaceEntities(
      state,
      userListingForSaleRefs
    ),

    listingsNotForSale:
      getMarketplaceEntities(
        state,
        userListingNotForSaleRefs
      ),

    reviews,
    queryReviewsError,

    forSalePagination,
    notForSalePagination,

    userListingForSaleRefsInProgress,
    userListingNotForSaleRefsInProgress,
  };
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProfilePageComponent);